import { useStrings } from "@1js/localization-components";
import {
  Button,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import * as React from "react";
import {
  DisplayCategoryKey,
  ExtensionKey,
} from "../PromptLibrary/PromptLibrary.keys";
import type { Appearance } from "../data";
import { ClearFiltersLabel } from "../sharedStrings";
import { ComboboxFilter } from "./Filters/ComboboxFilter";
import { DropdownFilter } from "./Filters/DropdownFilter";
import { MenuFilter } from "./Filters/MenuFilter";
import { useStyles } from "./PromptFilter.styles";
import type { Filter, FilterName } from "./types";

export type PromptFiltersProps = {
  /**
   * Optional appearance to be applied to control icon style
   */
  appearance?: Appearance;

  /**
   * The id of the FluentUI tab that's hosting this filter.
   */
  tab: string;

  /**
   * List of filters.
   */
  filters: Filter[];

  /**
   * Selected values for each filter.
   */
  selectedFilters?: Record<string, string[]>;

  /**
   * Default selected values for each filter.
   */
  defaultSelectedFilters?: Record<string, string[]>;

  /**
   * Optional loading state to be applied to the root element. If true, a skeleton view will be rendered.
   */
  isLoading?: boolean;

  /**
   * Optional toggle reflecting if declarative copilot is selected
   */
  isDeclarativeCopilotSelected?: boolean;

  /**
   * Callback to be called when the filter changes. Make sure no customer data is being passed back. The host usually logs the filters, so make sure no sensitive data is being passed back.
   * @param selectedValues The new selected values across all filters
   * @param filter The filters that changed
   * @returns void
   */
  onFilterChanged: (
    selectedValues: Record<string, string[]>,
    filters: Filter[]
  ) => void;

  /**
   * Optional callback to be called when the filter is opened or closed.
   * @param isOpen Whether filter is open or closed
   * @param filters The filters that changed
   * @returns void
   */
  onOpenChanged?: (isOpen: boolean, filters: Filter[]) => void;

  /**
   * Optional to render new filters in version 2 design
   */
  isMetaOsV2?: boolean;

  isRockSteadyUxEnabled?: boolean;

  /**
   * Optional disabled filters
   */
  disabledFilters?: Record<string, boolean>;

  /**
   * Optional selected filter value passed from home page
   */
  selectedFilterValue?: string | null;

  /**
   * Optional prop to handle clearing all selected filters
   */
  clearFilterHandler?: () => void;

  /**
   * Optional callbacks to register/de-register clear callback for each filter
   */
  registerClearCallback?: (key: FilterName, callback: () => void) => void;

  deregisterClearCallback?: (key: FilterName) => void;

  /**
   * Optional prop to determine if any clear callbacks are registered
   */
  anyCallbacksRegistered?: boolean;
};

/**
 * Render the proper filter based on the filter type.
 */
const renderFilter = (
  filter: Filter,
  tab: string,
  onFilterChanged: (
    selectedValues: Record<string, string[]>,
    filters: Filter[]
  ) => void,
  isLoading?: boolean,
  appearance?: Appearance,
  selectedFilters?: Record<string, string[]>,
  defaultSelectedFilters?: Record<string, string[]>,
  onOpenChanged?: (isOpen: boolean, filters: Filter[]) => void
) => {
  if (filter.hidden) {
    return <></>;
  }

  switch (filter.type) {
    case "combobox":
      return (
        <ComboboxFilter
          key={filter.key}
          filter={filter}
          isLoading={isLoading}
          tab={tab}
          appearance={appearance}
          defaultSelectedValues={defaultSelectedFilters?.[filter.key]}
          selectedValues={selectedFilters?.[filter.key]}
          onChange={(_, newValues) => {
            onFilterChanged(
              {
                ...selectedFilters,
                [filter.key]: newValues ?? [],
              },
              [filter]
            );
          }}
          onOpenChanged={onOpenChanged}
        />
      );
    default:
      return (
        <DropdownFilter
          key={filter.key}
          filter={filter}
          isLoading={isLoading}
          tab={tab}
          appearance={appearance}
          defaultSelectedValues={defaultSelectedFilters?.[filter.key]}
          selectedValues={selectedFilters?.[filter.key]}
          onChange={(_, newValues) => {
            onFilterChanged(
              {
                ...selectedFilters,
                [filter.key]: newValues ?? [],
              },
              [filter]
            );
          }}
          onOpenChanged={onOpenChanged}
        />
      );
  }
};

const renderFilterV2 = (
  filter: Filter,
  tab: string,
  onFilterChanged: (
    selectedValues: Record<string, string[]>,
    filters: Filter[]
  ) => void,
  isLoading?: boolean,
  appearance?: Appearance,
  selectedFilters?: Record<string, string[]>,
  defaultSelectedFilters?: Record<string, string[]>,
  onOpenChanged?: (isOpen: boolean, filters: Filter[]) => void,
  isMetaOsV2?: boolean,
  isRockSteadyUxEnabled?: boolean,
  disabledFilters?: Record<string, boolean>,
  selectedFilterValue?: string | null,
  registerClearCallback?: (key: FilterName, callback: () => void) => void,
  deregisterClearCallback?: (key: FilterName) => void
) => {
  if (filter.hidden) {
    return <></>;
  }

  return (
    <MenuFilter
      key={filter.key}
      filter={filter}
      isLoading={isLoading}
      tab={tab}
      appearance={appearance}
      defaultSelectedValues={defaultSelectedFilters?.[filter.key]}
      selectedValues={selectedFilters?.[filter.key] ?? []}
      onChange={(_, newValues) => {
        onFilterChanged(
          {
            ...selectedFilters,
            [filter.key]: newValues ?? [],
          },
          [filter]
        );
      }}
      onOpenChanged={onOpenChanged}
      isMetaOsV2={isMetaOsV2}
      disabledFilters={disabledFilters}
      isRockSteadyUxEnabled={isRockSteadyUxEnabled}
      selectedFilterValue={selectedFilterValue}
      registerClearCallback={registerClearCallback}
      deregisterClearCallback={deregisterClearCallback}
    />
  );
};

const useV2FilterStyles = makeStyles({
  container: {
    gap: tokens.spacingHorizontalM,
  },
});

export const PromptFilters: React.FC<PromptFiltersProps> = ({
  appearance,
  tab,
  filters,
  selectedFilters,
  defaultSelectedFilters,
  isLoading,
  isDeclarativeCopilotSelected,
  onFilterChanged,
  onOpenChanged,
  isMetaOsV2,
  isRockSteadyUxEnabled,
  disabledFilters,
  selectedFilterValue,
  clearFilterHandler,
  registerClearCallback,
  deregisterClearCallback,
  anyCallbacksRegistered,
}) => {
  const styles = useStyles(appearance)();
  const filterStyles = useV2FilterStyles();
  const localizations = useStrings({ ClearFiltersLabel });
  if (isDeclarativeCopilotSelected) {
    filters = filters.filter(
      (f) => f.key !== ExtensionKey && f.key !== DisplayCategoryKey
    );
  }

  return (
    <div
      className={mergeClasses(styles.root, filterStyles.container)}
      data-testid="prompt-filters"
    >
      {isMetaOsV2 || isRockSteadyUxEnabled
        ? filters.map((filter: Filter) => (
            <React.Fragment key={filter.key}>
              {renderFilterV2(
                filter,
                tab,
                onFilterChanged,
                isLoading,
                appearance,
                selectedFilters,
                defaultSelectedFilters,
                onOpenChanged,
                isMetaOsV2,
                isRockSteadyUxEnabled,
                disabledFilters,
                selectedFilterValue,
                registerClearCallback,
                deregisterClearCallback
              )}
            </React.Fragment>
          ))
        : filters.map((filter: Filter) => (
            <React.Fragment key={filter.key}>
              {renderFilter(
                filter,
                tab,
                onFilterChanged,
                isLoading,
                appearance,
                selectedFilters,
                defaultSelectedFilters,
                onOpenChanged
              )}
            </React.Fragment>
          ))}
      {(isRockSteadyUxEnabled || isMetaOsV2) &&
        clearFilterHandler &&
        anyCallbacksRegistered && (
          <Button appearance={"transparent"} onClick={clearFilterHandler}>
            {localizations.ClearFiltersLabel()}
          </Button>
        )}
    </div>
  );
};
